import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Truck } from "lucide-react";
import { NAV, BRAND } from "@/lib/data";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Pages that start with a dark hero — navbar needs white text when transparent
  const darkHeroPage =
    location.pathname === "/" ||
    location.pathname === "/track" ||
    location.pathname === "/fleet" ||
    location.pathname === "/faq" ||
    location.pathname === "/terms" ||
    location.pathname === "/privacy" ||
    location.pathname === "/copyright" ||
    location.pathname === "/testimonials" ||
    location.pathname === "/how-it-works" ||
    location.pathname === "/loyalty" ||
    location.pathname === "/partners" ||
    location.pathname === "/refund-policy" ||
    location.pathname.startsWith("/agreement-preview") ||
    location.pathname.startsWith("/market") ||
    location.pathname.startsWith("/blog") ||
    location.pathname.startsWith("/jobs/") ||
    location.pathname.startsWith("/driver/");

  // Use white text only when: not scrolled AND on a dark-hero page
  const useWhiteText = !scrolled && darkHeroPage;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      {/* ── Keyframes for ★Loyalty flame + shimmer ── */}
      <style>{`
        @keyframes flame-flicker {
          0%,100% { transform:scale(1) rotate(-4deg);opacity:1;filter:drop-shadow(0 0 3px #FF4D00); }
          15%     { transform:scale(1.3) rotate(3deg);opacity:0.8;filter:drop-shadow(0 0 7px #FF8C00); }
          30%     { transform:scale(0.9) rotate(-6deg);opacity:0.95; }
          50%     { transform:scale(1.25) rotate(5deg);opacity:0.72;filter:drop-shadow(0 0 10px #FFD700); }
          70%     { transform:scale(0.95) rotate(-3deg);opacity:0.88; }
          85%     { transform:scale(1.18) rotate(4deg);opacity:0.68;filter:drop-shadow(0 0 6px #FF6B00); }
        }
        @keyframes loyalty-shimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .loyalty-star {
          display:inline-block;
          animation: flame-flicker 2.0s ease-in-out infinite;
          transform-origin: bottom center;
          will-change: transform, opacity;
        }
        .loyalty-shimmer-text {
          background: linear-gradient(90deg,
            #FF4D00 0%, #FF7B00 18%, #FFD700 35%,
            #FF8C00 50%, #FFD700 65%, #FF7B00 82%, #FF4D00 100%
          );
          background-size: 300% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: loyalty-shimmer 3.2s linear infinite;
        }
        .nav-loyalty-link:hover .loyalty-shimmer-text { animation-duration: 1.2s; }
        .nav-loyalty-link:hover .loyalty-star { animation-duration: 0.8s; }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center">
        <Link to="/" data-testid="logo-link" className="flex-shrink-0 flex items-center group">
          <img
            src={useWhiteText ? "/hylas-icon-white.png" : "/hylas-icon.png"}
            alt="Hylas Companion"
            className="h-9 w-auto object-contain"
            style={{ maxWidth: 120 }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 flex-1 justify-center px-4">
          {/* ── Loyalty Program — FIRST position, flame orange ── */}
          <Link
            to="/loyalty"
            data-testid="nav-loyalty"
            className="nav-loyalty-link relative whitespace-nowrap font-head uppercase tracking-[0.12em] flex items-center gap-0.5"
            style={{ fontSize: "0.9rem", fontWeight: 700 }}
          >
            <span className="loyalty-star text-[11px] mr-0.5" style={{ color: "#FF4D00" }}>★</span>
            <span className="loyalty-shimmer-text">Loyalty</span>
          </Link>
          {NAV.map((n) => (
            <Link
              key={n.path}
              to={n.path}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`whitespace-nowrap font-head text-sm uppercase tracking-[0.15em] transition-colors duration-200 hover:text-primary ${
                location.pathname === n.path
                  ? "text-primary"
                  : useWhiteText ? "text-white" : "text-[#0A0A0A]"
              }`}
            >
              {n.label}
            </Link>
          ))}
          {/* ── Market special link ── */}
          <Link
            to="/market"
            data-testid="nav-market"
            className="market-pulse relative whitespace-nowrap font-head uppercase tracking-[0.12em]"
            style={{ color: "#F5C842", fontSize: "0.9rem", fontWeight: 700 }}
          >
            <span style={{
              position: "absolute", top: "-13px", left: "50%",
              transform: "translateX(-50%)",
              fontSize: "7px", fontWeight: 800, letterSpacing: "0.18em",
              background: "#F5C842", color: "#0A0A0A",
              padding: "1px 5px", lineHeight: "1.5", whiteSpace: "nowrap",
            }}>NEW</span>
            Market
          </Link>
        </nav>

        <div className="flex-shrink-0 flex items-center gap-3">
          <button
            data-testid="nav-apply-btn"
            onClick={() => navigate("/apply")}
            className="hidden sm:inline-flex font-head text-sm uppercase tracking-[0.15em] bg-primary text-black px-6 py-3 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300"
          >
            Apply Now
          </button>
          <button
            data-testid="mobile-menu-btn"
            className={`lg:hidden transition-colors duration-300 ${useWhiteText ? "text-white" : "text-[#0A0A0A]"}`}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden fixed top-20 left-0 right-0 bottom-0 z-40 bg-white overflow-y-auto overscroll-contain"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-1 pb-10">
            {/* Loyalty first in mobile too */}
            <Link
              to="/loyalty"
              data-testid="nav-loyalty-mobile"
              className="nav-loyalty-link font-head text-sm font-bold uppercase tracking-[0.15em] py-3 border-b border-border flex items-center gap-2"
            >
              <span className="loyalty-star" style={{ color: "#FF4D00" }}>★</span>
              <span className="loyalty-shimmer-text">Loyalty Program</span>
            </Link>
            {NAV.map((n) => (
              <Link
                key={n.path}
                to={n.path}
                className="font-head text-sm uppercase tracking-[0.15em] py-3 border-b border-border text-[#0A0A0A]"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/market"
              data-testid="nav-market-mobile"
              className="font-head text-sm font-bold uppercase tracking-[0.15em] py-3 border-b border-border flex items-center gap-2"
              style={{ color: "#F5C842" }}
            >
              Market
              <span className="text-[8px] font-black tracking-[0.2em] bg-[#F5C842] text-black px-1.5 py-0.5">NEW</span>
            </Link>
            <Link
              to="/apply"
              className="mt-3 text-center font-head text-sm uppercase tracking-[0.15em] bg-primary text-black py-3"
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
